/**
 * custom-roast-ui.js
 * Phaser cart delay banner — drives preorder / partial-backorder messaging.
 *
 * (Filename retained from CCC; roast product UI and roast calendar removed.)
 *
 * Cart page (snippets/custom-roast-cart-banner.liquid):
 *   [data-cru-cart-banner]     — banner wrapper
 *   [data-cru-cart-ready-date] — ready-date text
 *   [data-cru-cart-explanation] — explanation text
 */

import { getUnifiedCartMessage } from '@theme/custom-dispatch-manager';
import { ThemeEvents } from '@theme/events';

/**
 * Live cart delay state from the morphing cart-products island.
 * @returns {{ hasPreorders: boolean, preorderItems: Array<object> } | null}
 */
function readLiveCartDelayState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    const state = JSON.parse(el.textContent || '{}');
    return {
      hasPreorders: Boolean(state.hasPreorders),
      preorderItems: Array.isArray(state.preorderItems) ? state.preorderItems : [],
    };
  } catch (e) {
    console.warn('[Phaser] Failed to parse cart delay state', e);
    return null;
  }
}

async function applyCartBanner() {
  const bannerEl = document.querySelector('[data-cru-cart-banner]');
  if (!bannerEl) return;

  const live = readLiveCartDelayState();

  let hasPreorders = bannerEl.getAttribute('data-has-preorders') === 'true';

  /** @type {Array<{type?: string, vendor?: string, estimatedRestockDate?: string|null, kind?: string}>} */
  let preorderItems = [];

  if (live) {
    hasPreorders = live.hasPreorders;
    preorderItems = live.preorderItems;
    bannerEl.setAttribute('data-has-preorders', hasPreorders ? 'true' : 'false');
    bannerEl.toggleAttribute('hidden', !hasPreorders);

    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      preorderJsonEl.textContent = JSON.stringify(preorderItems);
    }
  } else {
    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      try {
        preorderItems = JSON.parse(preorderJsonEl.textContent || '[]');
      } catch (e) {
        console.warn('[Phaser] Failed to parse cart banner preorder items', e);
        preorderItems = [];
      }
    }

    if (!preorderItems.length) {
      const preorderTypesStr = bannerEl.getAttribute('data-preorder-types') || '';
      preorderItems = preorderTypesStr
        ? preorderTypesStr.split('|').map((type) => ({ type, vendor: '', estimatedRestockDate: null }))
        : [];
    }

    if (bannerEl.hasAttribute('hidden')) {
      getUnifiedCartMessage(false, []);
      return;
    }
  }

  if (!hasPreorders) {
    // Clear the shipping-estimator ready-window memo so cart-mode ETAs do not
    // keep anchoring on a removed preorder line.
    getUnifiedCartMessage(false, []);
    return;
  }

  const dateEl = bannerEl.querySelector('[data-cru-cart-ready-date]');
  const explanationEl = bannerEl.querySelector('[data-cru-cart-explanation]');

  const unifiedMsg = getUnifiedCartMessage(hasPreorders, preorderItems);

  if (dateEl && unifiedMsg.readyMessage && dateEl.textContent.trim() !== unifiedMsg.readyMessage.trim()) {
    dateEl.textContent = unifiedMsg.readyMessage;
  }
  if (
    explanationEl &&
    unifiedMsg.explanation &&
    explanationEl.textContent.trim() !== unifiedMsg.explanation.trim()
  ) {
    explanationEl.textContent = unifiedMsg.explanation;
  }
}

// Horizon 4.2.0 re-renders the cart section on a browser Back (bfcache / disk-cache
// restore) without dispatching cartLinesUpdate. The banner lives inside that section,
// so it comes back as the Liquid placeholder unless we repaint here. Called directly:
// the event fires after the morph, and rAF callbacks are parked in a hidden tab.
document.addEventListener(ThemeEvents.cartSectionRestored, () => {
  applyCartBanner();
});

import('@shopify/events').then(({ StandardEvents }) => {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(() => {
        requestAnimationFrame(() => applyCartBanner());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[Phaser] cart banner update failed:', error);
        }
      });
  });
});

applyCartBanner();
